import _styled, { default as _styled2 } from "styled-components";
var _templateObject;
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import classNames from "classnames";
import "inobounce";
import { action } from "mobx";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import combine from "terriajs-cesium/Source/Core/combine";
import Disclaimer from "../Disclaimer";
import DragDropFile from "../DragDropFile";
import ExplorerWindow from "../ExplorerWindow/ExplorerWindow";
import FeatureInfoPanel from "../FeatureInfo/FeatureInfoPanel";
import FeedbackForm from "../Feedback/FeedbackForm";
import { Medium, Small } from "../Generic/Responsive";
import SatelliteHelpPrompt from "../HelpScreens/SatelliteHelpPrompt";
import withFallback from "../HOCs/withFallback";
import ExperimentalFeatures from "./ExperimentalFeatures";
import { CollapsedNavigation } from "../Map/MapNavigation";
import HelpPanel from "../Map/Panels/HelpPanel/HelpPanel";
import PrintView from "../Map/Panels/SharePanel/Print/PrintView";
import TrainerBar from "./TrainerBar/TrainerBar";
import MobileHeader from "../Mobile/MobileHeader";
import MapInteractionWindow from "../Notification/MapInteractionWindow";
import Notification from "../Notification/Notification";
import Branding from "../SidePanel/Branding";
import FullScreenButton from "../SidePanel/FullScreenButton";
import SidePanel from "../SidePanel/SidePanel";
import StoryBuilder from "../Story/StoryBuilder";
import StoryPanel from "../Story/StoryPanel/StoryPanel";
import ClippingBoxToolLauncher from "../Tools/ClippingBox/ClippingBoxToolLauncher";
import Tool from "../Tools/Tool";
import TourPortal from "../Tour/TourPortal";
import WelcomeMessage from "../WelcomeMessage/WelcomeMessage";
import SelectableDimensionWorkflow from "../Workflow/SelectableDimensionWorkflow";
import WorkflowPanelPortal from "../Workflow/WorkflowPanelPortal";
import { ContextProviders } from "../Context";
import { GlobalTerriaStyles } from "./GlobalTerriaStyles";
import MapColumn from "../Map/MapColumn";
import processCustomElements from "./processCustomElements";
import SidePanelContainer from "./SidePanelContainer";
import Styles from "./standard-user-interface.scss";
import { terriaTheme } from "./StandardTheme";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const animationDuration = 250;
const StandardUserInterfaceBase = observer(props => {
  const {
    t
  } = useTranslation();
  const acceptDragDropFile = action(() => {
    props.viewState.isDraggingDroppingFile = true;
    // if explorer window is already open, we open my data tab
    if (props.viewState.explorerPanelIsVisible) {
      props.viewState.openUserData();
    }
  });
  const handleDragOver = e => {
    if (!e.dataTransfer.types || !e.dataTransfer.types.includes("Files")) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    acceptDragDropFile();
  };
  const shouldUseMobileInterface = () => {
    var _props$minimumLargeSc;
    return document.body.clientWidth < ((_props$minimumLargeSc = props.minimumLargeScreenWidth) !== null && _props$minimumLargeSc !== void 0 ? _props$minimumLargeSc : 768);
  };
  const resizeListener = action(() => {
    props.viewState.useSmallScreenInterface = shouldUseMobileInterface();
  });
  useEffect(() => {
    window.addEventListener("resize", resizeListener, false);
    return () => {
      window.removeEventListener("resize", resizeListener, false);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(resizeListener, [props.minimumLargeScreenWidth]);
  useEffect(() => {
    if (props.terria.configParameters.storyEnabled && props.terria.stories && props.terria.stories.length && !props.viewState.storyShown) {
      props.terria.notificationState.addNotificationToQueue({
        title: t("sui.notifications.title"),
        message: t("sui.notifications.message"),
        confirmText: t("sui.notifications.confirmText"),
        denyText: t("sui.notifications.denyText"),
        confirmAction: action(() => {
          props.viewState.storyShown = true;
        }),
        denyAction: action(() => {
          props.viewState.storyShown = false;
        }),
        type: "story",
        width: 300
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [props.terria.storyPromptShown]);

  // Merge theme in order of highest priority: themeOverrides props -> theme config parameter -> default terriaTheme
  const mergedTheme = combine(props.themeOverrides, combine(props.terria.configParameters.theme, terriaTheme, true), true);
  const theme = mergedTheme;
  const customElements = processCustomElements(props.viewState.useSmallScreenInterface, props.children);
  const showStoryBuilder = props.viewState.storyBuilderShown && !props.viewState.useSmallScreenInterface;
  const showStoryPanel = props.terria.configParameters.storyEnabled && props.terria.stories.length > 0 && props.viewState.storyShown && !props.viewState.explorerPanelIsVisible && !props.viewState.storyBuilderShown;
  return /*#__PURE__*/_jsxs(ContextProviders, {
    viewState: props.viewState,
    theme: mergedTheme,
    children: [/*#__PURE__*/_jsx(GlobalTerriaStyles, {}), /*#__PURE__*/_jsx(TourPortal, {}), /*#__PURE__*/_jsx(CollapsedNavigation, {}), /*#__PURE__*/_jsx(SatelliteHelpPrompt, {}), /*#__PURE__*/_jsx(Medium, {
      children: /*#__PURE__*/_jsx(SelectableDimensionWorkflow, {})
    }), /*#__PURE__*/_jsxs("div", {
      className: Styles.storyWrapper,
      children: [!props.viewState.disclaimerVisible && /*#__PURE__*/_jsx(WelcomeMessage, {}), /*#__PURE__*/_jsxs(_StyledDiv, {
        className: Styles.uiRoot,
        onDragOver: handleDragOver,
        $_css: props.viewState.disclaimerVisible && "filter: blur(10px);",
        children: [/*#__PURE__*/_jsx(_StyledDiv2, {
          className: Styles.ui,
          $_css2: theme.dark,
          children: /*#__PURE__*/_jsxs("div", {
            className: Styles.uiInner,
            children: [!props.viewState.hideMapUi && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(Small, {
                children: /*#__PURE__*/_jsx(MobileHeader, {
                  menuItems: customElements.menu,
                  menuLeftItems: customElements.menuLeft,
                  version: props.version
                })
              }), /*#__PURE__*/_jsx(Medium, {
                children: /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx(WorkflowPanelPortal, {
                    show: props.terria.isWorkflowPanelActive
                  }), /*#__PURE__*/_jsxs(SidePanelContainer, {
                    tabIndex: 0,
                    show: props.viewState.isMapFullScreen === false && props.terria.isWorkflowPanelActive === false,
                    children: [/*#__PURE__*/_jsx(FullScreenButton, {
                      minified: true,
                      animationDuration: 250,
                      btnText: t("addData.btnHide")
                    }), /*#__PURE__*/_jsx(Branding, {
                      version: props.version
                    }), /*#__PURE__*/_jsx(SidePanel, {})]
                  })]
                })
              })]
            }), /*#__PURE__*/_jsx(Medium, {
              children: /*#__PURE__*/_jsx("div", {
                className: classNames(Styles.showWorkbenchButton, {
                  [Styles.showWorkbenchButtonTrainerBarVisible]: props.viewState.trainerBarVisible,
                  [Styles.showWorkbenchButtonisVisible]: props.viewState.isMapFullScreen,
                  [Styles.showWorkbenchButtonisNotVisible]: !props.viewState.isMapFullScreen
                }),
                children: /*#__PURE__*/_jsx(FullScreenButton, {
                  minified: false,
                  btnText: t("sui.showWorkbench", {
                    count: props.viewState.terria.workbench.items.length
                  }),
                  animationDuration: animationDuration,
                  elementConfig: props.terria.elements.get("show-workbench")
                })
              })
            }), /*#__PURE__*/_jsxs("section", {
              className: Styles.map,
              children: [/*#__PURE__*/_jsx(MapColumn, {
                customElements: customElements,
                animationDuration: animationDuration
              }), /*#__PURE__*/_jsx("div", {
                id: "map-data-attribution"
              }), /*#__PURE__*/_jsxs("main", {
                children: [/*#__PURE__*/_jsx(ExplorerWindow, {}), props.terria.configParameters.experimentalFeatures && !props.viewState.hideMapUi && /*#__PURE__*/_jsx(ExperimentalFeatures, {
                  experimentalItems: customElements.experimentalMenu
                })]
              })]
            })]
          })
        }), !props.viewState.hideMapUi && /*#__PURE__*/_jsx(Medium, {
          children: /*#__PURE__*/_jsx(TrainerBar, {})
        }), /*#__PURE__*/_jsx(Medium, {
          children: props.viewState.isToolOpen && /*#__PURE__*/_jsx(Tool, _objectSpread({}, props.viewState.currentTool))
        }), props.viewState.panel, /*#__PURE__*/_jsx(Notification, {}), /*#__PURE__*/_jsx(MapInteractionWindow, {}), !customElements.feedback.length && props.terria.configParameters.feedbackUrl && !props.viewState.hideMapUi && props.viewState.feedbackFormIsVisible && /*#__PURE__*/_jsx(FeedbackForm, {}), /*#__PURE__*/_jsx("div", {
          className: classNames(Styles.featureInfo, props.viewState.topElement === "FeatureInfo" ? "top-element" : "", {
            [Styles.featureInfoFullScreen]: props.viewState.isMapFullScreen
          }),
          tabIndex: 0,
          onPointerDown: action(() => {
            props.viewState.topElement = "FeatureInfo";
          }),
          children: /*#__PURE__*/_jsx(FeatureInfoPanel, {})
        }), /*#__PURE__*/_jsx(DragDropFile, {}), showStoryPanel && /*#__PURE__*/_jsx("div", {
          className: classNames(Styles.storyPanel, props.viewState.topElement === "StoryPanel" ? "top-element" : "", {
            [Styles.storyPanelFullScreen]: props.viewState.isMapFullScreen
          }),
          tabIndex: 0,
          onPointerDown: action(() => {
            props.viewState.topElement = "StoryPanel";
          }),
          children: /*#__PURE__*/_jsx(StoryPanel, {})
        })]
      }), props.terria.configParameters.storyEnabled && showStoryBuilder && /*#__PURE__*/_jsx(StoryBuilder, {
        isVisible: showStoryBuilder,
        animationDuration: animationDuration
      }), props.viewState.showHelpMenu && props.viewState.topElement === "HelpPanel" && /*#__PURE__*/_jsx(HelpPanel, {}), /*#__PURE__*/_jsx(Disclaimer, {})]
    }), props.viewState.printWindow && /*#__PURE__*/_jsx(PrintView, {
      window: props.viewState.printWindow,
      closeCallback: () => props.viewState.setPrintWindow(null)
    }), /*#__PURE__*/_jsx(ClippingBoxToolLauncher, {
      viewState: props.viewState
    })]
  });
});
export const StandardUserInterface = withFallback(StandardUserInterfaceBase);
export default withFallback(StandardUserInterfaceBase);
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n              ", "\n            "])), p => p.$_css);
var _StyledDiv2 = _styled2("div").withConfig({
  displayName: "StandardUserInterface___StyledDiv2",
  componentId: "sc-wdxieu-0"
})(["background:", ";"], p => p.$_css2);